import { Button, Modal } from "ant-design-vue";
import { defineAsyncComponent } from "vue";
import lodash from "lodash";
import moment from "moment";
import {
    createFieldCheckbox,
    createFieldInput,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTemplateBuilder,
    createFieldTextArea,
    createFieldTreeSelect
} from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import { conditionFormChecker, copyForm, putField } from "../../../../utils/form";
import { callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import { callInform, callInformFull } from "../../inform/request/request.section";
import { randomUuid } from "../../../../utils/util";
import { createSearchOneCriteria, setIsCheck, setProductQty } from "./request.logic";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { callUser } from "../../storage-location/request/requestor/requestor.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

const InspectionOfficerList = defineAsyncComponent(() => import("./inspection-officer/InspectionOfficerList"));

const yesNo = [
    { id: "1", value: true, name: i18n.global.t("model.inspection.labels.isInspect") },
    { id: "2", value: false, name: i18n.global.t("model.inspection.labels.isNotInspect") }
];
export const callUserObjective = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OBJECTIVE", value: searchQuery })
        .then(items => items.filter(item => ["1", "2"].includes(item.key)));
export const callDepartmentImportExport = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/department/cacheFindAll", {
        where: [
            { name: "refDepartmentType.key", operator: "=", value: "main" },
            { openBucket: true, name: "nameTh", operator: "like", value: searchQuery },
            { logic: "OR", closeBucket: true, name: "fullCode", operator: "like", value: searchQuery },
            { openBucket: true, name: "isImport", operator: "=", value: true },
            { logic: "OR", name: "isExport", operator: "=", value: true },
            { logic: "OR", name: "isRegister", operator: "=", value: true },
            { closeBucket: true, logic: "OR", name: "isDocument", operator: "=", value: true },
        ]
    });
export const callDepartment = () => store.dispatch("resource/v1/master/department/cacheFindAllWithChildrenType", { type: "main" });
export const callRequestDoc = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REQUEST_DOC", value: searchQuery });
export const callInspectionLocation = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "INSPECT_LOCATION", value: searchQuery });
export const callSequestrate = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SEQUESTRATE", value: searchQuery });
export const callContactStaffType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "CONTACT_STAFF_TYPE", value: searchQuery });
export const callInspectionAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "INSPECTION_ATTACHMENT", value: searchQuery });
export const callRiskProcess = ({ data }) => store.dispatch("resource/v1/transaction/inspection/riskProcess", { data });

export const callStorageLocation = ({ searchQuery }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "isLocation", type: "Boolean", operator: "=", value: true }
    ];
    if (searchQuery) {
        where.push({ openBucket: true, name: "approveCode", type: "String", operator: "like", value: searchQuery });
        where.push({ closeBucket: true, logic: "OR", name: "name", type: "String", operator: "like", value: searchQuery });
    }

    return store
        .dispatch("resource/v1/transaction/storageLocation/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "name", "approveCode"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};

export const callInspection = ({ searchQuery, userInfo }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
        { openBucket: true, name: "refRelease.refLastRequestTemp.refStatus.key", type: "String", operator: "in", value: ["waitReleaseResult", "cancel"] },
        { closeBucket: true, logic: "OR", name: "refRelease.id", type: "UUID", operator: "=", value: null },
    ];
    where.push();
    if (!userInfo?.refOfficerLv) {
        where.push(
            { openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 1
            { name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { name: "refUserCompany.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "2" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "1" },
            {
                name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorize.id",
                type: "Getpath",
                operator: "=",
                value: "refUserCompany.id"
            },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" } // 1
        );
    }
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/inspection/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "approveCode", "refUserCompany.id", "refUserCompany.fullNameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};
export const callInspectionFull = ({ props, id }) => {
    const prefix = "refInspection.";
    const criteria = {
        ...createSearchOneCriteria({ props, id, prefix }),
        where: [{ name: "refInspection.id", type: "UUID", operator: "=", value: id }]
    };
    criteria.children.push({
        name: `${prefix}attachments`,
        select: ["*"],
        page: 0,
        size: 1000000,
        sort: [{ name: "createDate", type: "ASC" }]
    });
    return store.dispatch("resource/v1/transaction/request/search", { criteria }).then(page => page.data[0]);
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("model.inspection.refUserCompany"),
            dataIndex: "refInspectionTemp.refUserCompany.fullNameTh",
            align: "center"
        }),
        createColumnInput({ title: () => i18n.global.t("model.request.draftCode"), dataIndex: "draftCode", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.request.requestCode"), dataIndex: "requestCode", align: "center" }),
        createColumnInput({
            title: () => i18n.global.t("views.request.approveCode"),
            dataIndex: "refInspection.approveCode",
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("model.inspection.expireDate"),
            dataIndex: "refInspection.expireDate",
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("model.inspection.transportDate"),
            dataIndex: "refInspectionTemp.transportDate",
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("model.inspection.inspectionDatetime"),
            dataIndex: "refInspectionTemp.inspectionDatetime",
            align: "center",
            format: "DD/MM/YYYY HH:mm"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.inspection.refRequestDoc"),
            dataIndex: "refInspectionTemp.refRequestDoc.value",
            inputProp: () => ({ list: callRequestDoc, optLabel: "value" }),
            search: { name: "refInspectionTemp.refRequestDoc.id" },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.inform.refOperation"),
            dataIndex: "refInspectionTemp.refInformTemp.refOperation.value",
            inputProp: () => ({ list: callUserObjective, optLabel: "value" }),
            search: { name: "refInspectionTemp.refInformTemp.refOperation.id" },
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("views.request.inspectionInfo.refInformTemp"),
            dataIndex: "refInspectionTemp.refInformTemp.approveCode",
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.request.refRequestType"),
            dataIndex: "refRequestType.value",
            customRender: ({ record }) => (
                <div>
                    <span>{record.refRequestType?.value}</span>
                    {record.isSignCa ? <CIcon class="ml-1" file="sign.svg"></CIcon> : null}
                </div>
            ),
            inputProp: () => ({ list: callRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.cancelSequestrate.refDepartment"),
            dataIndex: "refDepartment.nameTh",
            inputProp: () => ({ list: callDepartmentImportExport, optLabel: "nameTh" }),
            search: { name: "refDepartment.id" },
            align: "center",
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.request.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callRequestStatus, optLabel: "value" }),
            search: { name: "refStatus.id" },
            align: "center"
        })
    ];
    return columns;
};

export const createField = ({ props, data, refs, key }) => {
    const { visibleRequestForm } = props;
    const fields = [];
    if (visibleRequestForm) {
        fields.push(...createFieldRequest({ props, data, refs, key }));
    }
    fields.push(...createFieldInspection({ props, data, refs, key }));
    return fields;
};

export const createFieldRequest = ({ props, data, refs, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refInspection
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("model.request.refRequestType") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./request.logic");
                                                cloneInspection({
                                                    props,
                                                    model: {
                                                        ...createForm({ props, data, refs }),
                                                        id: model.id,
                                                        version: model.version,
                                                        isActive: model.isActive,
                                                        refStatus: model.refStatus,
                                                        refRequestDoc: model.refRequestDoc,
                                                        draftCode: model.draftCode,
                                                        requestCode: model.requestCode
                                                    }
                                                });
                                            } else {
                                                cloneInspection({ props, model, id: model.refInspection?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callRequestType({ searchQuery }).then(d => d.filter(d2 => ["1", "2", "4"].includes(d2?.key))),
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspection`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.approveCode"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refInspection,
                            "onUpdate:value": value => {
                                const oldValue = model.refInspection;
                                model.refInspection = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.storageLocation.approveCode")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneInspection({ props, model, id: value?.id }),
                                        onCancel: () => {
                                            model.refInspection = oldValue;
                                        }
                                    });
                                } else {
                                    cloneInspection({ props, model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callInspection({ searchQuery, userInfo: store.state.resource.auth.info?.user }),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item.approveCode} - {item.refUserCompany?.fullNameTh}
                                </div>
                            ),
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.refRequestDoc`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.inspection.refRequestDoc"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refInspectionTemp?.refRequestDoc,
                            "onUpdate:value": value => {
                                model.refInspectionTemp.refRequestDoc = value;
                                model.refInspectionTemp.refInformTemp = null;
                                model.refDepartment = null;
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callRequestDoc({ searchQuery }).then(d => d.filter(doc => ["02", "16", "17", "18", "19"].includes(doc?.key))),
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.refInformTemp`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.inspection.refInformTemp"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refInspectionTemp?.refInformTemp,
                            "onUpdate:value": value => {
                                const oldValue = lodash.cloneDeep(model.refInspectionTemp?.refInformTemp);
                                model.refInspectionTemp.refInformTemp = null;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("views.request.approveCode") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            setInformFull({ model, id: value?.id });
                                        },
                                        onCancel: () => {
                                            model.refInspectionTemp.refInformTemp = oldValue;
                                        }
                                    });
                                } else {
                                    setInformFull({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callInform({
                                    searchQuery,
                                    userInfo: store.state.resource.auth.info?.user,
                                    isOfficerCreate: modelRef.isOfficerCreate,
                                    requestDocKeys: [modelRef.refInspectionTemp?.refRequestDoc?.key],
                                    isRefInspectionNull: true
                                }),
                            optLabel: "approveCode",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),


            createFieldInput({
                visible: ({ field }) => model.refInspection?.approveCode && conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspection.approveCode`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.approveCode"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspection.approveCode,
                            "onUpdate:value": value => (model.refInspection.approveCode = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),

            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forRequestRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToOfficer"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forRequestRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forRequestRemark[0].remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forReceiveRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToUserCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forReceiveRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forReceiveRemark[0].remark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const createFieldInspection = ({ props, data, key }) => {
    const { viewOnly, menuContext, dataKey, request } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}waitReceiveTitle`,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <h3 class="mt-1 mb-3 text-center">{i18n.global.t("model.inspection.labels.inspectionInfo")}</h3>
            },
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.unlockRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 8 } }),
                label: () => i18n.global.t("model.inspection.unlockRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspectionTemp?.unlockRemark,
                            "onUpdate:value": value => (model.refInspectionTemp.unlockRemark = value),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.isInspect`,
                colProp: () => ({ xs: 24, md: 9 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 11 }, wrapperCol: { xs: 24, md: 13 } }),
                label: () => i18n.global.t("model.inspection.isInspect"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value:
                                model.refInspectionTemp?.isInspect === true
                                    ? yesNo.find(d => d.id === "1")
                                    : model.refInspectionTemp?.isInspect === false
                                    ? yesNo.find(d => d.id === "2")
                                    : undefined,
                            "onUpdate:value": value => {
                                onChangeIsInspect({ data, model, value });
                            },
                            disabled: viewOnly,
                            list: () => yesNo,
                            optLabel: "name"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}`) }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}buttonRiskValidate`,
                colProp: () => ({ xs: 24, md: 4 }),
                formItemRender: () => {
                    return () =>
                        model.refInspectionTemp.isRiskValidated && model.refInspectionTemp?.isInspectRiskValidated === true ? (
                            <div className="text-danger mt-2">{yesNo.find(d => d.id === "1").name}</div>
                        ) : model.refInspectionTemp.isRiskValidated && model.refInspectionTemp?.isInspectRiskValidated === false ? (
                            <div className="text-danger mt-2">{yesNo.find(d => d.id === "2").name}</div>
                        ) : (
                            <Button
                                class="btn btn-primary"
                                onClick={() => {
                                    setIsLoadingApp(true);
                                    return callRiskProcess({ data: model.refInspectionTemp })
                                        .finally(() => setIsLoadingApp(false))
                                        .then(riskProcess => {
                                            model.refInspectionTemp.isRiskValidated = true;
                                            model.refInspectionTemp.isInspectRiskValidated = !!riskProcess.isInspect;
                                            const isInspection = !!riskProcess.isInspect;
                                            const value = isInspection ? yesNo.find(d => d.id === "1") : yesNo.find(d => d.id === "2");
                                            onChangeIsInspect({ data, model, value });
                                        });
                                }}
                                disabled={viewOnly}
                            >
                                {i18n.global.t("model.inspection.isRiskValidated")}
                            </Button>
                        );
                }
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <div></div>
            },
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldTextArea({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.inspectRemark`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.inspectRemark"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.inspectRemark,
                                        "onUpdate:value": value => (model.refInspectionTemp.inspectRemark = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                        createFieldRadioGroup({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.refSequestrate`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.refSequestrate"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.refSequestrate,
                                        "onUpdate:value": value => {
                                            model.refInspectionTemp.refSequestrate = value;
                                        },
                                        disabled: viewOnly,
                                        list: ({ searchQuery }) =>
                                            callSequestrate({ searchQuery }).then(items => items.filter(item => ["1", "2"].includes(item.key))),
                                        optLabel: "value"
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                        }),
                        createFieldCheckbox({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.isSampling`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.isSampling"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        checked: modelRef.refInspectionTemp?.isSampling,
                                        "onUpdate:checked": checked => (modelRef.refInspectionTemp.isSampling = checked),
                                        disabled: viewOnly,
                                        text: () => i18n.global.t("model.inspection.isSampling")
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        })
                    ]
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldRadioGroup({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.refInspectLocation`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.refInspectLocation"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.refInspectLocation,
                                        "onUpdate:value": value => {
                                            model.refInspectionTemp.refInspectLocation = value;
                                            if (modelRef.refInspectionTemp?.refInspectLocation?.key === "1") {
                                                modelRef.refInspectionTemp.refStorageLocation = null;
                                            } else if (modelRef.refInspectionTemp?.refInspectLocation?.key === "2") {
                                                modelRef.refInspectionTemp.refInspectDepartment = null;
                                            }
                                        },
                                        disabled: viewOnly,
                                        list: callInspectionLocation,
                                        optLabel: "value"
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                        }),
                        createFieldTreeSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.refInspectDepartment`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.refInspectDepartment"),
                            inputProp: ({ field }) => {
                                return lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.refInspectDepartment,
                                        "onUpdate:value": value => (model.refInspectionTemp.refInspectDepartment = value),
                                        disabled: viewOnly,
                                        list: callDepartment,
                                        renderLabel: ({ item }) => (
                                            <div>
                                                {item.fullCode} : {item.nameTh}
                                            </div>
                                        ),
                                        filterWhere: ({ value }) => [
                                            { name: "fullCode", operator: "like", value },
                                            { logic: "OR", name: "nameTh", operator: "like", value }
                                        ],
                                        treeDefaultExpandAll: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                );
                            },
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.nameTh`) }
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.refStorageLocation`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.refStorageLocation"),
                            inputProp: ({ field }) => {
                                return lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.refStorageLocation,
                                        "onUpdate:value": value => {
                                            model.refInspectionTemp.refStorageLocation = value;
                                        },
                                        disabled: viewOnly,
                                        list: callStorageLocation,
                                        optLabel: "name",
                                        renderLabel: ({ item }) => {
                                            return (
                                                <div>
                                                    {item?.name} ({item?.approveCode})
                                                </div>
                                            );
                                        }
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                );
                            },
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.name`) }
                        }),
                        createFieldTextArea({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refInspectionTemp.locationRemark`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.inspection.locationRemark"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refInspectionTemp?.locationRemark,
                                        "onUpdate:value": value => (model.refInspectionTemp.locationRemark = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        })
                    ]
                })
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.refContactStaffType`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.inspection.refContactStaffType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspectionTemp?.refContactStaffType,
                            "onUpdate:value": value => {
                                model.refInspectionTemp.refContactStaffType = value;
                                onChangeContactStaffType({ model });
                            },
                            disabled: viewOnly,
                            list: callContactStaffType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}InspectionOfficerList`,
                colProp: () => ({ xs: 24, class: "mb-3" }),
                formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refInspectionTemp.officers`,
                            fetchCriteria: data.officer?.fetchCriteria,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly,
                            inspectionKey: dataKey,
                            request
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "InspectionOfficerList" } })
                    );
                    return <InspectionOfficerList {...componentProp}></InspectionOfficerList>;
                }
            },
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.abnormalRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.inspection.abnormalRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspectionTemp?.abnormalRemark,
                            "onUpdate:value": value => (model.refInspectionTemp.abnormalRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionTemp.officerRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.inspection.officerRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspectionTemp?.officerRemark,
                            "onUpdate:value": value => (model.refInspectionTemp.officerRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const cloneInspection = ({ props, model, id }) => {
    if (id) {
        callInspectionFull({ props, id }).then(request => {
            const inspection = request.refInspection;
            const refInspectionTemp = copyForm({ form: { ...inspection, isTemp: true } });
            if (id) {
                refInspectionTemp?.attachments?.forEach((d, index) => (d.refRealId = inspection.attachments[index].id));
                refInspectionTemp?.products?.forEach((d, index) => {
                    d.refReal = { id: inspection.products[index].id };
                    d.productQties?.forEach((d2, index2) => (d2.refReal = { ...inspection.products[index].productQties[index2] }));
                });
            }
            router.push({
                name: router.currentRoute.value.name,
                params: { props: JSON.stringify({ form: { ...model, refDepartment: inspection?.refDepartment, refInspectionTemp } }) },
                query: { _timestamp: new Date().getTime() }
            });
            return callUser({ userCompanyId: refInspectionTemp.refUserCompany.id }).then(users => {
                refInspectionTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id) || refInspectionTemp.refUser;
                router.push({
                    name: router.currentRoute.value.name,
                    params: { props: JSON.stringify({ form: { ...model, refDepartment: inspection?.refDepartment, refInspectionTemp } }) },
                    query: { _timestamp: new Date().getTime() }
                });
            });
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};

export const setInformFull = ({ model, id }) => {
    if (id) {
        callInformFull({ id }).then(inform => {
            model.refInspectionTemp.refInformTemp = inform;
            model.refInspectionTemp.vehicleNo = inform?.vehicleNo;
            model.refInspectionTemp.containerNo = inform?.containerNo;
            model.refInspectionTemp.sealNo = inform?.sealNo;
            model.refDepartment = inform?.refExportCheckpoint;
            model.refInspectionTemp.refDepartment = inform?.refExportCheckpoint;
            model.refInspectionTemp.refUserCompany = inform?.refUserCompany;
            lodash.remove(model.refInspectionTemp.products, row => !row.id);
            model.refInspectionTemp.products.forEach(row => (row.isDelete = true));
            const products = model.refInspectionTemp?.refInformTemp?.products?.map(d => {
                d.setIsCheck = setIsCheck;
                const len = d.productQties?.length || 0;
                const productQties = [];

                const transferDeclarationProduct = inform.refTransferDeclaration?.products?.find(it => it.invoiceCode === d.invoiceCode);
                transferDeclarationProduct?.productQties?.forEach(qty => {
                    qty.docName = i18n.global.t("model.informProductQty.labels.transferDeclarationDoc");
                    d.productQties?.push(qty);
                });

                for (let i = 0; i < len; i++) {
                    d.productQties[i].docName = i18n.global.t("model.informProductQty.labels.referDoc");
                    const qty = lodash.cloneDeep(d.productQties[i]);
                    qty.docName = i18n.global.t("model.informProductQty.labels.inspectionDoc");
                    qty.enable = true;
                    // qty.amount = 0;
                    // qty.netWeight = 0;
                    // qty.numberOfPackage = 0;
                    // qty.packageSize = 0;
                    // qty.qty = 0;
                    qty.setProductQty = setProductQty;
                    //qty.refInformProductQty = lodash.cloneDeep(d.productQties[i]),
                    qty.isSave = false;

                    d.productQties?.push(qty);
                    productQties.push({
                        ...copyForm({ form: qty }),
                        refInformProductQty: lodash.cloneDeep(d.productQties[i]),
                        isSave: true
                    });
                }
                return {
                    refInformProduct: d,
                    isCheck: false,
                    isSave: true,
                    productQties: productQties
                };
            });
            model.refInspectionTemp.products = [...model.refInspectionTemp.products, ...products];
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};

const onChangeIsInspect = ({ data, model, value }) => {
    if (value.id === "1") {
        model.refInspectionTemp.isInspect = true;
        model.refInspectionTemp.refContactStaffType = data.contactStaffTypes.find(d => d.key === "1");
        onChangeContactStaffType({ model });
    } else {
        model.refInspectionTemp.isInspect = value.id === "2" ? false : undefined;
        model.refInspectionTemp.inspectRemark = null;
        model.refInspectionTemp.locationRemark = null;
        model.refInspectionTemp.refInspectLocation = null;
        model.refInspectionTemp.refInspectDepartment = null;
        model.refInspectionTemp.refStorageLocation = null;
        model.refInspectionTemp.refSequestrate = null;
        model.refInspectionTemp.refContactStaffType = data.contactStaffTypes.find(d => d.key === "2");
        onChangeContactStaffType({ model });
    }
};

const onChangeContactStaffType = ({ model }) => {
    if (model.refInspectionTemp?.refContactStaffType?.key === "1") {
        lodash.remove(model.refInspectionTemp.officers, d => !d.id);
        if (model.refInspectionTemp?.officers?.length === 0) {
            model.refInspectionTemp?.officers?.push({
                _id: randomUuid(),
                isSave: true,
                refUser: store.state.resource.auth.info?.user,
                tel: null
            });
        }
    } else if (model.refInspectionTemp?.refContactStaffType?.key === "2") {
        model.refInspectionTemp?.officers?.forEach(d => (d.isDelete = true));
    }
};
